/**
 * AI Connection Suggestions Engine
 * Pure client-side heuristic AI — no external API required.
 * Analyses family member data + memorial tree data to surface
 * likely connections using: shared surnames, birth year proximity,
 * location/bio keyword overlap, and memorial linkage patterns.
 */

import { DEFAULT_FAMILY_TREES } from '../components/Memorial/FamilyTree/familyTreeData';
import { mockMemorials } from '../data/mockData';

// ── Helpers ───────────────────────────────────────────────────────────────────
const lastName = (name = '') => (name.trim().split(/\s+/).pop() || '').toLowerCase();
const firstWord = (name = '') => (name.trim().split(/\s+/)[0] || '').toLowerCase();

function extractLocations(bio = '') {
  const patterns = [
    /\bin\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/g,
    /\bfrom\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/g,
    /\b([A-Z][a-z]+,\s*[A-Z]{2})\b/g,
  ];
  const locs = new Set();
  for (const pat of patterns) {
    let m;
    while ((m = pat.exec(bio)) !== null) locs.add(m[1].toLowerCase());
  }
  return [...locs];
}

function bioKeywords(bio = '') {
  const stopwords = new Set(['the', 'and', 'was', 'for', 'his', 'her', 'who', 'with',
    'has', 'had', 'she', 'him', 'are', 'from', 'that', 'this', 'they', 'not', 'but']);
  return bio.toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 4 && !stopwords.has(w));
}

function overlap(arrA, arrB) {
  const setB = new Set(arrB);
  return arrA.filter(x => setB.has(x)).length;
}

function confidence(score) {
  if (score >= 80) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}

// ── Core Analyser ─────────────────────────────────────────────────────────────
export function generateAISuggestions(userMembers = []) {
  const suggestions = [];
  const now = Date.now();

  // Gather all tree nodes from all memorials
  const allTreeNodes = [];
  Object.entries(DEFAULT_FAMILY_TREES).forEach(([memId, tree]) => {
    const memorial = mockMemorials.find(m => m.id === memId);
    tree.nodes.forEach(node => {
      allTreeNodes.push({ ...node, memorialId: memId, memorialName: memorial?.name || memId });
    });
  });

  // Pre-compute per-member data
  const memberData = userMembers.map(m => ({
    ...m,
    lastName: lastName(m.full_name),
    firstName: firstWord(m.full_name),
    locs: extractLocations(m.bio || ''),
    keywords: bioKeywords(m.bio || ''),
  }));

  const existingNames = new Set(userMembers.map(m => m.full_name?.toLowerCase()));

  // ── PASS 1: User member ↔ Memorial tree node matching ─────────────────────
  memberData.forEach(member => {
    allTreeNodes.forEach(node => {
      if (existingNames.has(node.name?.toLowerCase())) return; // already in network

      let score = 0;
      const signals = [];

      // Shared last name
      const nodeLast = lastName(node.name);
      if (member.lastName && nodeLast && member.lastName === nodeLast) {
        score += 40;
        signals.push(`Shared surname "${nodeLast}"`);
      }

      // Birth year proximity (±15 yrs suggests same generation)
      if (member.birth_year && node.birthYear) {
        const diff = Math.abs(Number(member.birth_year) - node.birthYear);
        if (diff <= 5) { score += 30; signals.push(`Very close birth years (${node.birthYear})`); }
        else if (diff <= 15) { score += 15; signals.push(`Similar birth era (${node.birthYear})`); }
      }

      // Bio keyword overlap
      const nodeKeywords = bioKeywords(node.bio || '');
      const kwOverlap = overlap(member.keywords, nodeKeywords);
      if (kwOverlap >= 3) { score += 20; signals.push(`${kwOverlap} shared life keywords`); }
      else if (kwOverlap >= 1) { score += 8; signals.push(`Overlapping life details`); }

      // Location overlap
      const nodeLocs = extractLocations(node.bio || '');
      const locOverlap = overlap(member.locs, nodeLocs);
      if (locOverlap >= 1) { score += 15; signals.push(`Shared location mention`); }

      // Linked memorial match
      if (member.linked_memorial_id && member.linked_memorial_id === node.memorialId) {
        score += 25;
        signals.push(`Both linked to "${node.memorialName}"`);
      }

      if (score >= 30) {
        suggestions.push({
          id: `ai-${now}-${Math.random().toString(36).slice(2, 9)}`,
          type: 'tree_match',
          sourceMemberId: member.id,
          sourceMemberName: member.full_name,
          suggestedName: node.name,
          suggestedRelationship: node.relation,
          confidenceScore: Math.min(score, 99),
          confidenceLabel: confidence(score),
          reason: `${node.name} appears in the ${node.memorialName} memorial tree and may be connected to ${member.full_name}.`,
          signals,
          memorialId: node.memorialId,
          memorialName: node.memorialName,
          nodeData: node,
        });
      }
    });
  });

  // ── PASS 2: User member ↔ User member cross-matching ──────────────────────
  for (let i = 0; i < memberData.length; i++) {
    for (let j = i + 1; j < memberData.length; j++) {
      const a = memberData[i];
      const b = memberData[j];
      // Already have a relationship — skip
      if (a.relationship === b.relationship && a.linked_memorial_id === b.linked_memorial_id) continue;

      let score = 0;
      const signals = [];

      if (a.lastName && b.lastName && a.lastName === b.lastName) {
        score += 35; signals.push(`Both share surname "${a.lastName}"`);
      }
      if (a.birth_year && b.birth_year) {
        const diff = Math.abs(Number(a.birth_year) - Number(b.birth_year));
        if (diff <= 5) { score += 20; signals.push('Very close birth years'); }
        else if (diff <= 25) { score += 10; signals.push('Similar generation'); }
      }
      const kwOv = overlap(a.keywords, b.keywords);
      if (kwOv >= 2) { score += 15; signals.push(`${kwOv} shared life themes`); }
      const locOv = overlap(a.locs, b.locs);
      if (locOv >= 1) { score += 15; signals.push('Shared location'); }
      if (a.linked_memorial_id && a.linked_memorial_id === b.linked_memorial_id) {
        score += 20; signals.push(`Both linked to "${a.linked_memorial_name || a.linked_memorial_id}"`);
      }

      if (score >= 40) {
        suggestions.push({
          id: `ai-${now}-${Math.random().toString(36).slice(2, 9)}`,
          type: 'member_pair',
          sourceMemberId: a.id,
          sourceMemberName: a.full_name,
          suggestedName: b.full_name,
          suggestedRelationship: null,
          confidenceScore: Math.min(score, 99),
          confidenceLabel: confidence(score),
          reason: `${a.full_name} and ${b.full_name} share several characteristics that suggest they may be related.`,
          signals,
          memorialId: a.linked_memorial_id || b.linked_memorial_id || null,
          memorialName: a.linked_memorial_name || b.linked_memorial_name || null,
          pairedMemberId: b.id,
        });
      }
    }
  }

  // ── PASS 3: Orphan detection — members with no memorial link ──────────────
  const unlinked = memberData.filter(m => !m.linked_memorial_id);
  unlinked.forEach(member => {
    mockMemorials.forEach(memorial => {
      const tree = DEFAULT_FAMILY_TREES[memorial.id];
      if (!tree) return;
      const nameParts = (member.full_name || '').toLowerCase().split(/\s+/);
      const treeNames = tree.nodes.map(n => n.name.toLowerCase());
      const hasNameMatch = treeNames.some(tn =>
        nameParts.some(part => part.length > 3 && tn.includes(part))
      );
      if (hasNameMatch) {
        suggestions.push({
          id: `ai-${now}-${Math.random().toString(36).slice(2, 9)}`,
          type: 'memorial_link',
          sourceMemberId: member.id,
          sourceMemberName: member.full_name,
          suggestedName: memorial.name,
          suggestedRelationship: 'Possible connection',
          confidenceScore: 55,
          confidenceLabel: 'medium',
          reason: `${member.full_name}'s name appears to match individuals in the "${memorial.name}" memorial tree. Consider linking this member.`,
          signals: ['Name match found in memorial tree'],
          memorialId: memorial.id,
          memorialName: memorial.name,
        });
      }
    });
  });

  // Deduplicate by suggestedName + sourceMemberId, sort by confidence
  const seen = new Set();
  return suggestions
    .filter(s => {
      const key = `${s.sourceMemberId}-${s.suggestedName}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => b.confidenceScore - a.confidenceScore)
    .slice(0, 20); // cap at 20 suggestions
}